import dayjs from 'dayjs';
import moment from 'moment';
import * as yup from 'yup';

import { SelectOptions } from '@src/components/FormComponent/types';
import { ResponseProps } from '@src/model/GlobalStyles';

export const checkPermissions = (permission: string): boolean => {
  return permission ? true : false;
};

export const filterNumeric = (input: string) => {
  return input.replace(/[^\d]/g, '');
};

export function cleanPhoneNumber(input: string) {
  if (input) {
    const cleanedInput = input.replace(/(?!^\+)\D/g, '');
    return cleanedInput.startsWith('+') ? cleanedInput : '+' + cleanedInput;
  }

  return input;
}

export function getTimeFromTimestamp(timestamp: string) {
  const date = new Date(timestamp);

  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();

  const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

  return formattedTime;
}

export function obscureInput(input: string) {
  if (input.includes('@')) {
    const [username, domain] = input.split('@');

    if (username.length > 2) {
      const obscuredUsername = username.substring(0, 2) + '*'.repeat(username.length - 2);
      return `${obscuredUsername}@${domain}`;
    } else {
      return input;
    }
  } else {
    return input;
  }
}

export function capitalizeFirstLetter(name?: string) {
  if (!name) return name;

  if (name.includes('_')) {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

export function formatDate(dateString: string | Date): string {
  const date = new Date(dateString);

  const monthNames: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const day: number = date.getDate();
  const month: string = monthNames[date.getMonth()];
  const year: string = date.getFullYear().toString().slice(-2);

  const formattedDate = `${day} ${month}, ${year}`;

  return formattedDate;
}

export function formatDates(startDate: string, endDate: string) {
  if (startDate === '') {
    return '';
  }

  if (startDate === undefined) {
    return '-';
  }

  function getDate(date: Date) {
    const day = new Date(date).getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    return `${day} ${month}`;
  }

  const formattedStartDate = getDate(new Date(startDate));
  const formattedEndDate = getDate(new Date(endDate));

  const year = new Date(endDate).getFullYear().toString().slice(-2);

  if (formattedEndDate === formattedStartDate) {
    return formatDate(new Date(startDate));
  } else {
    return `${formattedStartDate} - ${formattedEndDate}, ${year}`;
  }
}

export const formatNumberDate = (date: string | Date) => {
  const currentDate = new Date(date || new Date());
  const year = currentDate.getFullYear();
  const day = currentDate.getDate();
  const month = currentDate.getMonth();

  return `${year}-${month.toString().length === 1 ? `0${month + 1}` : month + 1}-${day}`;
};

export function formatsNumber(num: number | string, fix?: boolean) {
  const parsedNumber = typeof num === 'string' ? parseFloat(num) : num;

  if (isNaN(parsedNumber)) {
    return num;
  }

  let formattedNumber: string;

  if (fix) {
    formattedNumber = parsedNumber.toFixed(2);
  } else {
    formattedNumber = parsedNumber.toString();
  }

  const parts = formattedNumber.split('.');

  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  formattedNumber = fix ? parts.join('.') : parts[0];

  return formattedNumber;
}

export const removeDuplicates = (arr: string[]) => {
  return arr.filter((value, index, self) => {
    return self.indexOf(value) === index;
  });
};

export const dateFormatter = (dateStr: string) => {
  const parsedDate = moment(dateStr);

  return parsedDate.format('YYYY-MM-DD');
};

export const getUniqueRecords = <T extends { _id?: string }>(
  previous: ResponseProps<T> | undefined,
  response: { data: ResponseProps<T> }
) => {
  const { data } = response || {};
  const { page, totalPages, results } = data || {};
  const existingResultIds = new Set(previous?.results?.map(result => result._id));

  const updatedResults = results
    ? [...(previous?.results || []), ...results.filter(newResult => !existingResultIds.has(newResult._id))]
    : previous?.results || [];

  return {
    page: page ?? previous?.page,
    totalPages: totalPages ?? previous?.totalPages,
    results: updatedResults,
  };
};

export const preventInvalidChars = (e: React.KeyboardEvent<HTMLDivElement>) => {
  if (['e', 'E', '-', '+'].includes(e.key)) {
    e.preventDefault();
  }
};

export const getSelectValidation = (label: string, isValueNumber?: boolean, requiredMessage?: string) =>
  yup
    .mixed()
    .label(label)
    .required(label + ' is required')
    .test('is-string-or-object', requiredMessage || label + ' is required', value => {
      if (typeof value === 'string') return true;

      return (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as SelectOptions).label === 'string' &&
        typeof (value as SelectOptions).value === (isValueNumber ? 'number' : 'string')
      );
    });

export const getColor = (label: string) => {
  if (['Conference', '1 person', 'none', 'full-day', 'illumination'].includes(label)) {
    return '#053E56';
  }

  if (['Hotel', '2 persons', 'audio-video', '1-hour', 'occupancy'].includes(label)) {
    return '#09B6CE';
  }

  if (['4 persons', 'video-only', '2-hour'].includes(label)) {
    return '#50c3b6';
  }

  if (['6 persons', 'audio-only', '4-hour'].includes(label)) {
    return '#032D3F';
  }

  if (['8 persons'].includes(label)) {
    return '#1A5B75';
  } else {
    return '#185f5f';
  }
};

export const getUserPermissiong = (role: string) => {
  switch (role) {
    case 'org_manager':
    case 'super_admin':
      return ['bookings', 'rooms', 'locations', 'scheduling', 'devices', 'users', 'manageOrganizations', 'home'];
    case 'global_manager':
      return ['bookings', 'rooms', 'locations', 'scheduling', 'users'];
    case 'local_manager':
      return ['bookings', 'rooms', 'scheduling', 'locations', 'users'];
    case 'room_display':
      return ['room_display'];
    default:
      return ['bookings', 'rooms', 'scheduling', 'locations', 'users'];
  }
};

export const time24to12hr = (time24hr: string) => dayjs(time24hr, 'HH:mm').format('h:mm A').replace('30', '00');
export function formatDateRecuring(date: string): string {
  const tempData = new Date(date);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = days[tempData.getDay()];
  const month = months[tempData.getMonth()];
  const dateNum = tempData.getDate();

  const suffix =
    dateNum % 10 === 1 && dateNum !== 11
      ? 'st'
      : dateNum % 10 === 2 && dateNum !== 12
      ? 'nd'
      : dateNum % 10 === 3 && dateNum !== 13
      ? 'rd'
      : 'th';

  return `${day} ${month} ${dateNum}${suffix}`;
}

export const formatNumber = (num: number): string => {
  const units = ['', 'K', 'M', 'B', 'T'];
  let unitIndex = 0;

  while (num >= 1000 && unitIndex < units.length - 1) {
    num /= 1000;
    unitIndex++;
  }

  const formatted = num.toFixed(units[unitIndex] ? 1 : 0);
  return unitIndex > 0 ? `+ ${formatted}${units[unitIndex]}` : formatted;
};

export function getTimeInTimeZone(offset: string): string {
  const now = new Date();

  // Extract hours and minutes from the offset
  const match = offset.match(/([+-])(\d{2}):(\d{2})/);

  if (!match) {
    // throw new Error("Invalid time zone format. Expected format: ±HH:MM");
    return '';
  }

  const sign = match[1] === '-' ? -1 : 1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3], 10);

  const totalOffsetMinutes = sign * (hours * 60 + minutes);

  const localTime = new Date(now.getTime() + totalOffsetMinutes * 60 * 1000);

  return localTime.toISOString().substring(11, 19);
}
